import React, {useMemo, useState} from 'react'
import {Button, Icon, Loading, Pagination, Tabs} from '@therms/atalaya'
import {
  LocationBoloCard,
  useCurrentUserAnPCheck,
  useLocationBolos,
  useLocationContext,
  usePaginationState,
  useTabsState,
} from '@therms/react-modules'
import {BoloFormModal} from '@src/modules/Locations/components/BoloFormModal'
import {stringOrUndefined} from '@src/utils/strings'
import {IoMdAdd} from 'react-icons/io'
import {MdWarningAmber} from 'react-icons/md'

function BoloList() {
  const {location, locationId} = useLocationContext(true)
  const [showBoloModal, setShowBoloModal] = useState<string | boolean>(false)
  const [tab, setTab] = useTabsState(`loc-bolos-tab`, `active`)
  const {recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords} =
    usePaginationState('loc-bolos', [tab])

  const canCreateBolos = useCurrentUserAnPCheck(
    (user) => user.anp.role.ops.permissions.createBolos,
  )

  const locationBolosArgs = useMemo(() => {
    const now = new Date().toISOString()

    let args: any = {
      locationId,
      pagination: {
        limit: recordsPerPage,
        skip: skippedRecords,
      },
    }

    if (tab === 'active') {
      args = {...args, active: true}
    } else if (tab === 'future') {
      args = {
        ...args,
        active: false,
        activeDate: {gt: now},
      }
    } else if (tab === 'expired') {
      args = {
        ...args,
        active: false,
        expireDate: {lt: now},
      }
    }

    return args
  }, [locationId, recordsPerPage, skippedRecords, tab])

  const {data, loading, refresh} = useLocationBolos(locationBolosArgs)

  return (
    <>
      <div className="gap-y-lg relative flex flex-col">
        <div className="gap-y-base flex w-full flex-row-reverse flex-wrap items-end justify-between">
          {location?.active && canCreateBolos && (
            <Button
              className="mt-base w-full sm:mt-0 sm:w-auto"
              onClick={() => setShowBoloModal(true)}
              variant="main"
            >
              <Icon Icon={IoMdAdd} size="sm" />
              New Bolo
            </Button>
          )}

          <Tabs initialTabKey={tab} onTabClick={setTab} pills>
            <Tabs.Tab tabKey="active" title="Active" />
            <Tabs.Tab tabKey="future" title="Future" />
            <Tabs.Tab tabKey="expired" title="Expired" />
          </Tabs>
        </div>

        <div className="relative">
          {loading && (
            <Loading
              message="Loading Bolos"
              messagePosition="vertical"
              overlay
              size="xxl"
            />
          )}

          {data?.bolos.length ? (
            <div className="gap-y-base flex flex-col">
              {data.bolos.map((bolo) => (
                <LocationBoloCard
                  bolo={bolo}
                  key={bolo.id}
                  onEdit={
                    location?.active && canCreateBolos ? () => setShowBoloModal(bolo.id) : undefined
                  }
                  status={tab}
                />
              ))}

              <Pagination
                onPageClick={setSkippedRecords}
                onSetRecordsPerPage={setRecordsPerPage}
                recordsPerPage={recordsPerPage}
                showDescription
                skippedRecords={skippedRecords}
                totalRecords={data?.total}
              />
            </div>
          ) : (
            <div className="flex h-full grow flex-col items-center opacity-50">
              <Icon
                Icon={MdWarningAmber}
                color="neutral"
                className="h-[100px] w-[100px]"
              />
              <span className="text-center text-lg font-medium capitalize">
                No {tab} Bolos
              </span>
            </div>
          )}
        </div>
      </div>

      {showBoloModal && (
        <BoloFormModal
          boloId={stringOrUndefined(showBoloModal)}
          closeHandler={() => setShowBoloModal(false)}
          onComplete={refresh}
        />
      )}
    </>
  )
}

export {BoloList}
